import useAsync from "@/hooks/useAsync";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import * as React from "react";
import useApi from "@/hooks/useApi";
import { SiteSettings } from "./types";
import { TimeZone } from "@/types";
import useToastAlert from "@/hooks/useToastAlert";
import { SiteContext } from "@/context/SiteContext";
import { Site } from "@/types";
import SearchBar from "@/components/SearchBar";
import GeneralSettings from "./components/GeneralSettings";
import UserAccessSettings from "./components/UserAccessSettings";
import AccountSecuritySettings from "./components/AccountSecuritySettings";
import ManageDataPrivacySettings from "./components/ManageDataPrivacySettings";
import TranscriptPurgingSettings from "./components/TranscriptPurgingSettings";
import SupportAccessSettings from "./components/SupportAccessSettings";

export default function Index() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [searchFilter, setSearchFilter] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState("general");
  const [primaryLanguage, setPrimaryLanguage] = React.useState("en");
  const { site: contextSite } = React.useContext(SiteContext);
  const [site, setSite] = React.useState<Site>(contextSite);

  // Update local state when context changes (after SiteProvider loads)
  React.useEffect(() => {
    setSite(contextSite);
  }, [contextSite]);

  // Reload site data function for after save
  const reloadSiteData = async () => {
    try {
      // Reload site data (company info) from velaro-admin
      const siteResponse = await api.admin.get("Site");
      const siteData = await siteResponse.json();
      setSite(siteData);

      // Reload toggle data from velaro-messaging
      const settingsResponse = await api.messaging.get("SiteSettings");
      const settingsData = await settingsResponse.json();
      setSiteSettingsAsync(settingsData);

      return { site: siteData, settings: settingsData };
    } catch (error) {
      console.error("Failed to reload site data:", error);
    }
  };

  React.useEffect(() => {
    const handleSearch = (event: CustomEvent) => {
      setSearchFilter(event.detail.toLowerCase());
    };
    window.addEventListener("accountSearch", handleSearch as EventListener);
    return () =>
      window.removeEventListener(
        "accountSearch",
        handleSearch as EventListener
      );
  }, []);

  const {
    pending: loadingSiteSettings,
    value: siteSettings,
    setValue: setSiteSettingsAsync
  } = useAsync<SiteSettings>({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.messaging.get("SiteSettings");
      return await response.json();
    }
  });

  // Create a wrapper function that handles the null case
  const setSiteSettings = React.useCallback(
    (updater: React.SetStateAction<SiteSettings>) => {
      setSiteSettingsAsync((prevValue) => {
        if (!prevValue) return prevValue;

        if (typeof updater === "function") {
          return updater(prevValue);
        }
        return updater;
      });
    },
    [setSiteSettingsAsync]
  );

  const { pending: loadingTimezones, value: timeZones } = useAsync<TimeZone[]>({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.messaging.get("Timezones");
      return await response.json();
    }
  });

  const { pending: loadingCountries, value: countries } = useAsync<
    { countryID: string; countryName: string }[]
  >({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.admin.get("Countries");
      return await response.json();
    }
  });

  async function save() {
    try {
      // Save site data (company info) to velaro-admin
      const response = await api.admin.post("Site", site);

      // Save siteSettings data (toggles) to velaro-messaging
      const siteSettingsResponse = await api.messaging.post("SiteSettings", siteSettings);

      if (response.ok && siteSettingsResponse.ok) {
        displayToast(Severity.Success, "Account settings saved successfully");
        await reloadSiteData();
      } else {
        displayToast(Severity.Error, "Failed to save account settings");
      }
    } catch (error) {
      displayToast(Severity.Error, "Failed to save account settings");
    }
  }

  const timeZoneOptions: Record<string, string> = {};
  timeZones?.forEach((tz) => {
    timeZoneOptions[tz.id] = tz.displayName;
  });

  const countryOptions: Record<string, string> = {};

  countries?.forEach((country) => {
    countryOptions[country.countryID] = country.countryName;
  });

  if (
    loadingSiteSettings ||
    loadingTimezones ||
    loadingCountries ||
    !siteSettings
  ) {
    return <LoadingSpinner />;
  }

  const isVisible = (text: string) => {
    if (!searchFilter) return true;
    return text.toLowerCase().includes(searchFilter);
  };

  return (
    <>
      <style>{`
        body *:not(#root):not(#root *):has(.text-lg):has([placeholder="Search"]) {
          display: none !important;
        }
        main > *:first-child:not(.sticky):not([class*="sticky"]) {
          display: none !important;
        }
      `}</style>
      <div>
        <div className="sticky z-10 bg-gray-50" style={{ top: "-12px" }}>
          <div className="relative">
            <div
              className="absolute -top-6 left-0 right-0 h-6 bg-gray-50"
              aria-hidden="true"
            />
            <div className="-m-6 px-6 pt-3">
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-medium text-slate-800">
                  Account
                </div>
                <PrimaryBlueButton label="Save" onClick={save} />
                {/* <SearchBar
                  onSearch={(searchText) => {
                    window.dispatchEvent(
                      new CustomEvent("accountSearch", { detail: searchText })
                    );
                  }}
                  placeholderText="Search"
                /> */}
              </div>
              <div className="flex justify-between items-center border-b-2 border-slate-200 mb-4 w-full mt-8">
                <div className="flex">
                  <button
                    className={
                      selectedTab === "general"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("general")}
                  >
                    General
                  </button>
                  {/* <button
                    className={
                      selectedTab === "user-access"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("user-access")}
                  >
                    User Access
                  </button> */}
                  <button
                    className={
                      selectedTab === "manage-data-privacy"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("manage-data-privacy")}
                  >
                    Manage Data Privacy
                  </button>
                  {/* <button
                    className={
                      selectedTab === "transcript-purging"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("transcript-purging")}
                  >
                    Transcript Purging
                  </button>
                  <button
                    className={
                      selectedTab === "account-security"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("account-security")}
                  >
                    Account Security
                  </button>
                  <button
                    className={
                      selectedTab === "support-access"
                        ? "text-[#3383EC] p-2 border-b-2 px-5 border-[#3383EC]"
                        : "p-2 px-5"
                    }
                    onClick={() => setSelectedTab("support-access")}
                  >
                    Support Access
                  </button> */}
                </div>
              </div>
            </div>
          </div>
          <div className="h-4 bg-gray-50" aria-hidden="true" />
        </div>

        <div className="p-4 mt-2">
          {selectedTab === "general" && (
            <GeneralSettings
              site={site}
              setSite={setSite}
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              timeZoneOptions={timeZoneOptions}
              countryOptions={countryOptions}
              isVisible={isVisible}
            />
          )}
          {/* {selectedTab === "user-access" && (
            <UserAccessSettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )} */}
          {selectedTab === "manage-data-privacy" && (
            <ManageDataPrivacySettings
              site={site}
              setSite={setSite}
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
          {/* {selectedTab === "transcript-purging" && (
            <TranscriptPurgingSettings />
          )}
          {selectedTab === "account-security" && (
            <AccountSecuritySettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
          {selectedTab === "support-access" && <SupportAccessSettings />} */}
        </div>
      </div>
    </>
  );
}
